import React, { useEffect } from "react";
import styled from "styled-components";
import MainNav from "../navbar/MainNav";
import EastIcon from "@mui/icons-material/East";
import { motion, useAnimation } from "framer-motion";
import { analytics } from "../../authentication/firebase"; // Import analytics
import { logEvent } from "firebase/analytics"; // Import logEvent

// Left Leaves
import leftLeafBg from "../../assets/hero-section2/left-leaf-bg.png";
import leftMidLeaf from "../../assets/hero-section2/left-mid-leaf.png";
import leftRightLeaf from "../../assets/hero-section2/left-right-leaf.png";
import leftLeftLeaf from "../../assets/hero-section2/left-left-leaf.png";
import leftLgLeafWithBranch from "../../assets/hero-section2/left-lg-leaf-with-branch.png";
import leftLeafWithBranch from "../../assets/hero-section2/left-leaf-with-branch.png";

// Not used, just place holder for now
import leftSmLeaves from "../../assets/hero-section/left-sm-leaves.png";
import leftSmallLeaves from "../../assets/hero-section/left-small-leaves.png";
import leftBgLeafWithBranch from "../../assets/hero-section/left-bg-leaf-with-branch.png";

// Right Leaves
import rightLeafWithBranch from "../../assets/hero-section2/right-leaf-with-branch.png";
import overflowLeaf from "../../assets/hero-section2/right-bg-leaves.png";
import rightLgLeafWithBranch from "../../assets/hero-section2/right-lg-leaf-with-branch.png";
import rightSmLeaves from "../../assets/hero-section2/right-sm-leaves.png";
import rightBgLeaves from "../../assets/hero-section2/right-bg-leaves.png";

// Not used, just place holder for now
import rightLongLeaf1 from "../../assets/hero-section/right-long-leaf-1.png";
import rightLongLeaf2 from "../../assets/hero-section/right-long-leaf-2.png";
import right3Leaves from "../../assets/hero-section/right-3-leaves.png";
import right2Leaves from "../../assets/hero-section/right-2-leaves.png";

// Middle Leaves
import midLeftLeaf from "../../assets/hero-section2/mid-left-leaf.png";
import midLeaves from "../../assets/hero-section2/mid-leaves.png";
import midRightLeaf from "../../assets/hero-section2/mid-right-leaf.png";

// Trees
import treeRight from "../../assets/hero-section2/tree-right.png";
import treeLgLeft from "../../assets/hero-section2/tree-lg-left.png";
import treeSmLeft from "../../assets/hero-section2/tree-sm-left.png";

// Clouds
import rightCloud from "../../assets/hero-section2/Right Cloud.svg";
import middleCloud from "../../assets/hero-section2/Middle Cloud.svg";
import leftCloud from "../../assets/hero-section2/Left Cloud.svg";

// Shapes
import shape1 from "../../assets/hero-section2/shape1.png";
import shape2 from "../../assets/hero-section2/shape2.png";
import shape3 from "../../assets/hero-section2/shape3.png";
import shape4 from "../../assets/hero-section2/shape4.png";
import shape5 from "../../assets/hero-section2/shape5.png";
import shape6 from "../../assets/hero-section2/shape6.png";
import { useNavigate } from "react-router";

// Reusable animation components
const AnimatedImage = ({
  src,
  alt,
  className,
  initial,
  animate,
  transition,
  continuousAnimation,
  ...props
}) => {
  const controls = useAnimation();


  useEffect(() => {
    const sequence = async () => {
      // First run the entrance animation
      if (controls && controls.start) {
        await controls.start(animate);

        if (continuousAnimation) {
          // Start continuous animation from the final state of entrance animation
          await controls?.start({
            ...animate, // Keep the final state of entrance animation
            ...continuousAnimation.animate, // Add continuous animation properties
            transition: {
              ...continuousAnimation.transition,
              repeat: Infinity,
            },
          });
        }
      }
    };
    sequence();
  }, [controls, animate, continuousAnimation]);

  return (
    <motion.img
      src={src}
      alt={alt}
      className={className}
      initial={initial}
      animate={controls}
      transition={transition}
      loading="lazy"
      {...props}
    />
  );
};

// Animation variants
const fadeIn = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  transition: { duration: 0.5, ease: "easeOut" },
};

const slideIn = (direction, delay = 0) => ({
  initial: { opacity: 0, x: direction === "left" ? -100 : 100 },
  animate: { opacity: 1, x: 0 },
  transition: { duration: 1.5, ease: "easeOut", delay },
});

const slideUp = (delay = 0) => ({
  initial: { opacity: 0, y: 50 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.5, ease: "easeOut", delay },
});

const floatAnimation = (duration = 3, delay = 0) => ({
  initial: { y: 0 },
  animate: {
    y: [0, -10, 0],
    rotate: [0, 5, 0],
  },
  transition: {
    duration: duration,
    repeat: Infinity,
    ease: "easeInOut",
    delay: delay,
  },
});

const swayAnimation = (duration = 4, delay = 0) => ({
  initial: { rotate: 0 },
  animate: {
    rotate: [0, 10, 0],
  },
  transition: {
    duration: duration,
    repeat: Infinity,
    ease: "easeInOut",
    delay: delay,
  },
});

const gentleFloat = (duration = 5, delay = 0) => ({
  initial: { y: 0 },
  animate: {
    y: [0, -5, 0],
    rotate: [0, 3, 0],
  },
  transition: {
    duration: duration,
    repeat: Infinity,
    ease: "easeInOut",
    delay: delay,
  },
});

const branchFloat = (duration = 6, delay = 0) => ({
  initial: { y: 0, rotate: 0 },
  animate: {
    y: [0, -8, 0],
    rotate: [0, 4, 0],
  },
  transition: {
    duration: duration,
    repeat: Infinity,
    ease: "easeInOut",
    delay: delay,
  },
});

const branchSway = (duration = 7, delay = 0) => ({
  initial: { rotate: 0 },
  animate: {
    rotate: [0, 6, 0],
  },
  transition: {
    duration: duration,
    repeat: Infinity,
    ease: "easeInOut",
    delay: delay,
  },
});

const cloudFloat = (duration = 8, delay = 0) => ({
  initial: { y: 0 },
  animate: {
    y: [0, -20, 0],
    x: [0, 10, 0],
  },
  transition: {
    duration: duration,
    repeat: Infinity,
    ease: "easeInOut",
    delay: delay,
  },
});

export default function HeroSection() {
  const navigate = useNavigate();

  const handleLearnMoreClick = () => {
    logEvent(analytics, 'learn_more_click', {
      page_title: document.title,
      page_location: window.location.href
    });
    navigate("/about-us")
  };

  return (
    <StyledHeroSection>
      <MainNav />
      <div className="hero-section-container">
        <motion.div
          className="content"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <motion.h6
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            Everlasting
          </motion.h6>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            Your engagement today helps preserve tomorrow 
          </motion.p>
          <motion.button
            className="learn-more primary-btn"
            onClick={handleLearnMoreClick}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            Learn More <EastIcon />
          </motion.button>
        </motion.div>
        <div className="graphics">
          <div className="top">
            <AnimatedImage
              src={leftCloud}
              alt="Left Cloud"
              className="left-cloud"
              {...slideIn("left", 0.2)}
              continuousAnimation={cloudFloat(8, 0.2)}
            />
            <AnimatedImage
              src={middleCloud}
              alt="Middle Cloud"
              className="middle-cloud"
              {...fadeIn}
              continuousAnimation={cloudFloat(10, 0.4)}
            />
            <AnimatedImage
              src={rightCloud}
              alt="Right Cloud"
              className="right-cloud"
              {...slideIn("right", 0.2)}
              continuousAnimation={cloudFloat(9, 0.3)}
            />
          </div>
          <div className="left-section">
            <AnimatedImage
              src={treeLgLeft}
              alt="Large Tree"
              className="tree-large"
              initial={{ opacity: 0, y: 50, x: -10 }}
              animate={{ opacity: 1, y: 0, x: 0 }}
              transition={{ duration: 0.5, ease: "easeOut", delay: 0.5 }}
            />
            <AnimatedImage
              src={treeSmLeft}
              alt="Small Tree"
              className="tree-small"
              initial={{ opacity: 0, y: 50, x: -20 }}
              animate={{ opacity: 1, y: 0, x: 0 }}
              transition={{ duration: 0.5, ease: "easeOut", delay: 0.7 }}
            />
            <AnimatedImage
              src={leftSmallLeaves}
              alt="Left Small Leaves"
              className="left-small-leaves"
              initial={{ opacity: 0, x: -10, y: 10 }}
              animate={{ opacity: 1, x: 0, y: 0 }}
              transition={{ duration: 0.5, ease: "easeOut", delay: 0.8 }}
              continuousAnimation={gentleFloat(4, 0.2)}
            />
            <AnimatedImage
              src={leftSmLeaves}
              alt="Left Tiny Leaves"
              className="tiny-leaves"
              initial={{ opacity: 0, x: -10, y: 10 }}
              animate={{ opacity: 1, x: 0, y: 0 }}
              transition={{ duration: 0.5, ease: "easeOut", delay: 0.8 }}
              continuousAnimation={floatAnimation(3, 0.3)}
            />
            <AnimatedImage
              src={leftLeftLeaf}
              alt="Left Left Leaf"
              className="left-left-leaf"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, ease: "easeOut", delay: 0.6 }}
              continuousAnimation={swayAnimation(5, 0.4)}
            />
            <AnimatedImage
              src={leftRightLeaf}
              alt="Left Right Leaf"
              className="left-right-leaf"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, ease: "easeOut", delay: 0.6 }}
              continuousAnimation={floatAnimation(4, 0.5)}
            />
            <AnimatedImage
              src={leftMidLeaf}
              alt="Left Middle Leaf"
              className="left-mid-leaf"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, ease: "easeOut", delay: 0.6 }}
              continuousAnimation={gentleFloat(6, 0.6)}
            />
            <AnimatedImage
              src={leftBgLeafWithBranch}
              alt="Left Background Leaf with Branch"
              className="left-bg-leaf-with-branch"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, ease: "easeOut", delay: 1 }}
              continuousAnimation={branchFloat(6, 0.3)}
            />
            <AnimatedImage
              src={leftLeafBg}
              alt="Left Leaf Background"
              className="left-leaf-bg"
              initial={{ opacity: 0, x: -10, y: 50 }}
              animate={{ opacity: 1, x: 0, y: 0 }}
              transition={{ duration: 0.5, ease: "easeOut", delay: 0.7 }}
            />
            <AnimatedImage
              src={leftLgLeafWithBranch}
              alt="Left Large Leaf with Branch"
              className="left-lg-leaf-with-branch"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, ease: "easeOut", delay: 0.6 }}
              continuousAnimation={branchSway(7, 0.4)}
            />
            <AnimatedImage
              src={shape1}
              alt="Shape 1"
              className="shape-1"
              {...fadeIn}
              transition={{ ...fadeIn.transition, delay: 0.3 }}
            />
            <AnimatedImage
              src={shape2}
              alt="Shape 2"
              className="shape-2"
              {...fadeIn}
              transition={{ ...fadeIn.transition, delay: 0.7 }}
            />
            <AnimatedImage
              src={shape3}
              alt="Shape 3"
              className="shape-3"
              {...fadeIn}
              transition={{ ...fadeIn.transition, delay: 0.7 }}
            />
          </div>
          <div className="right-section">
            <AnimatedImage
              src={treeRight}
              alt="Right Tree"
              className="tree-right"
              initial={{ opacity: 0, y: 50, x: 10 }}
              animate={{ opacity: 1, y: 0, x: 0 }}
              transition={{ duration: 0.5, ease: "easeOut", delay: 0.5 }}
            />
            <AnimatedImage
              src={rightBgLeaves}
              alt="Background Leaves"
              className="bg-leaves"
              initial={{ opacity: 0, y: 20, x: 10 }}
              animate={{ opacity: 1, y: 0, x: 0 }}
              transition={{ duration: 0.5, ease: "easeOut", delay: 0.7 }}
              continuousAnimation={branchSway(5, 0.5)}
            />
            <AnimatedImage
              src={rightLgLeafWithBranch}
              alt="Large Leaf"
              className="large-leaf"
              initial={{ opacity: 0, x: 5, y: 20 }}
              animate={{ opacity: 1, x: 0, y: 0 }}
              transition={{ duration: 0.5, ease: "easeOut", delay: 0.7 }}
              continuousAnimation={branchFloat(7, 0.4)}
            />
            <AnimatedImage
              src={rightLeafWithBranch}
              alt="Right Leaf with Branch"
              className="right-leaf-with-branch"
              initial={{ opacity: 0, x: 5, y: 20 }}
              animate={{ opacity: 1, x: 0, y: 0 }}
              transition={{ duration: 0.5, ease: "easeOut", delay: 0.9 }}
            />
            <AnimatedImage
              src={rightSmLeaves}
              alt="Small Leaves"
              className="right-small-leaves"
              initial={{ opacity: 0, x: 5, y: 10 }}
              animate={{ opacity: 1, x: 0, y: 0 }}
              transition={{ duration: 0.5, ease: "easeOut", delay: 0.8 }}
              continuousAnimation={floatAnimation(4, 0.2)}
            />
            <AnimatedImage
              src={right3Leaves}
              alt="Three Leaves"
              className="three-leaves"
              initial={{ opacity: 0, x: -10, y: 10 }}
              animate={{ opacity: 1, x: 0, y: 0 }}
              transition={{ duration: 0.5, ease: "easeOut", delay: 0.8 }}
              continuousAnimation={swayAnimation(5, 0.3)}
            />
            <AnimatedImage
              src={right2Leaves}
              alt="Two Leaves"
              className="two-leaves"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, ease: "easeOut", delay: 0.6 }}
              continuousAnimation={gentleFloat(6, 0.4)}
            />
            <AnimatedImage
              src={rightLongLeaf1}
              alt="Long Leaf 1"
              className="long-leaf-1"
              initial={{ opacity: 0, x: 5, y: 10 }}
              animate={{ opacity: 1, x: 0, y: 0 }}
              transition={{ duration: 0.5, ease: "easeOut", delay: 0.6 }}
              continuousAnimation={floatAnimation(4, 0.5)}
            />
            <AnimatedImage
              src={rightLongLeaf2}
              alt="Long Leaf 2"
              className="long-leaf-2"
              initial={{ opacity: 0, x: -5, y: 10 }}
              animate={{ opacity: 1, x: 0, y: 0 }}
              transition={{ duration: 0.5, ease: "easeOut", delay: 0.6 }}
              continuousAnimation={swayAnimation(5, 0.6)}
            />
            <AnimatedImage
              src={shape4}
              alt="Shape 4"
              className="shape-4"
              {...fadeIn}
              transition={{ ...fadeIn.transition, delay: 0.7 }}
            />
            <AnimatedImage
              src={shape5}
              alt="Shape 5"
              className="shape-5"
              {...fadeIn}
              transition={{ ...fadeIn.transition, delay: 0.6 }}
            />
            <AnimatedImage
              src={shape6}
              alt="Shape 6"
              className="shape-6"
              {...fadeIn}
              transition={{ ...fadeIn.transition, delay: 0.3 }}
            />
          </div>
          <div className="middle-section">
            <AnimatedImage
              src={midLeaves}
              alt="Middle Leaves"
              className="mid-leaves"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, ease: "easeOut", delay: 0.7 }}
              continuousAnimation={gentleFloat(5, 0.2)}
            />
            <AnimatedImage
              src={midLeftLeaf}
              alt="Middle Left Leaf"
              className="mid-left-leaf"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, ease: "easeOut", delay: 0.8 }}
              continuousAnimation={floatAnimation(4, 0.3)}
            />
            <AnimatedImage
              src={midRightLeaf}
              alt="Middle Right Leaf"
              className="mid-right-leaf"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, ease: "easeOut", delay: 0.8 }}
              continuousAnimation={swayAnimation(6, 0.4)}
            />
          </div>
        </div>
      </div>
      <AnimatedImage
        src={leftLeafWithBranch}
        alt="Left Leaf with Branch"
        className="left-leaf-with-branch"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5, ease: "easeOut", delay: 0.6 }}
        continuousAnimation={branchFloat(5, 0.5)}
      />
      <AnimatedImage
        src={overflowLeaf}
        alt="Overflow Leaf"
        className="right-overflow-leaves"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5, ease: "easeOut", delay: 0.9 }}
        continuousAnimation={branchSway(6, 0.6)}
      />
    </StyledHeroSection>
  );
}

const StyledHeroSection = styled.div`
  min-height: 100vh;
  background: linear-gradient(250.89deg, #b2ebf2 -7.74%, #dcedc8 51.74%);
  background-color: coral;
  padding: 20px 0;
  margin-bottom: var(--section-margin);
  position: relative;
  .left-leaf-with-branch {
    position: absolute;
    left: -2%;
    bottom: -14%;
    width: 15%;
    rotate: 10deg;
    z-index: 16;
  }

  .right-overflow-leaves {
    position: absolute;
    right: -1%;
    bottom: -10%;
    width: 15%;
    rotate: 30deg;
    z-index: 1;
  }
  .hero-section-container {
    width: 100%;
    height: 80vh;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;

    .content {
      z-index: 20;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-direction: column;
      gap: 14px;
      text-align: center;
      h6 {
        color: var(--text-light-color);
        font-weight: 1000;
        font-size: 3.5rem;
        line-height: 1.2;
        margin-bottom: 1rem;
      }
      p {
        max-width: 45ch;
        text-align: center;
        font-size: 1.2rem;
        margin-bottom: 2rem;
        color: var(--text-dark-color);
      }

      .learn-more {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
        padding: 0.8rem 2.5rem;
        transition: transform 0.2s;
      }
    }

    .graphics {
      position: absolute;
      width: 100%;
      height: 100%;
      top: 0;
      left: 0;
      z-index: 1;
      overflow: hidden;
      pointer-events: none;
      .top {
        position: relative;
        width: 100%;
        height: 100%;

        .left-cloud {
          position: absolute;
          left: 10%;
          top: 22%;
          width: 250px;
          opacity: 0.8;
          z-index: 1;
        }

        .middle-cloud {
          position: absolute;
          left: 45%;
          top: 10%;
          width: 150px;
          z-index: 1;
        }

        .right-cloud {
          position: absolute;
          right: 25%;
          top: 15%;
          width: 250px;
          opacity: 0.2;
          z-index: 0;
        }
      }

      .left-section {
        position: absolute;
        left: 0;
        bottom: 0;
        width: 45%;
        height: 70vh;

        .tree-large {
          position: absolute;
          left: -18%;
          bottom: 20%;
          width: 60%;
          z-index: 11;
        }

        .tree-small {
          position: absolute;
          right: -100%;
          top: 30%;
          rotate: 5deg;
          width: 16%;
          z-index: 2;
        }

        .left-bg-leaf-with-branch {
          position: absolute;
          left: 35%;
          bottom: 0%;
          width: 1%;
          z-index: 5;
        }

        .left-leaf-bg {
          position: absolute;
          left: -1%;
          bottom: -1%;
          width: 60%;
          z-index: 15;
        }

        .left-small-leaves {
          position: absolute;
          left: 40%;
          bottom: 5%;
          width: 25%;
          z-index: 5;
        }

        .tiny-leaves {
          position: absolute;
          left: 45%;
          bottom: 1%;
          /* width: 80px; */
          z-index: 5;
        }

        .left-left-leaf {
          position: absolute;
          left: -1%;
          bottom: -20%;
          width: 10%;
          z-index: 16;
        }

        .left-right-leaf {
          position: absolute;
          left: 30%;
          bottom: -6%;
          width: 18%;
          z-index: 15;
        }

        .left-mid-leaf {
          position: absolute;
          left: 40%;
          bottom: -4%;
          width: 18%;
          z-index: 16;
        }
        .left-lg-leaf-with-branch {
          position: absolute;
          left: 0%;
          bottom: -15%;
          width: 43%;
          z-index: 16;
        }

        .shape-1 {
          position: absolute;
          left: 0;
          bottom: 5%;
          width: 110vw;
          /* height: 40vh; */
          z-index: 10;
        }

        .shape-2 {
          position: absolute;
          left: -15%;
          bottom: 13%;
          width: 60%;
          z-index: 11;
        }

        .shape-3 {
          position: absolute;
          left: 60%;
          bottom: 20%;
          width: 40%;
          z-index: 12;
        }
      }

      .right-section {
        position: absolute;
        right: 0;
        bottom: 0;
        width: 45%;
        height: 70vh;

        .tree-right {
          position: absolute;
          right: -20%;
          bottom: 30%;
          width: 70%;
          z-index: 10;
        }

        .bg-leaves {
          position: absolute;
          right: 2%;
          bottom: 0%;
          width: 25%;
          z-index: 16;
        }

        .large-leaf {
          position: absolute;
          right: 0%;
          bottom: -5%;
          width: 20%;
          rotate: 1deg;
          z-index: 17;
        }

        .right-small-leaves {
          position: absolute;
          right: 45%;
          bottom: -7%;
          width: 10%;
          z-index: 15;
        }

        .three-leaves {
          position: absolute;
          right: 35%;
          bottom: 13%;
          width: 5%;
          z-index: 5;
        }

        .two-leaves {
          position: absolute;
          right: -5%;
          bottom: -5%;
          width: 18%;
          z-index: 8;
        }

        .long-leaf-1 {
          position: absolute;
          right: 12%;
          bottom: -6%;
          width: 5%;
          z-index: 7;
        }

        .long-leaf-2 {
          position: absolute;
          right: 1%;
          bottom: -4%;
          width: 5%;
          z-index: 6;
        }

        .shape-4 {
          position: absolute;
          right: -15%;
          bottom: 10%;
          width: 60%;
          opacity: 0.6;
          z-index: 13;
        }

        .shape-5 {
          position: absolute;
          right: 0%;
          bottom: 0%;
          width: 100vw;
          /* height: 25vh; */
          opacity: 0.6;
          z-index: 14;
        }

        .shape-6 {
          position: absolute;
          right: 0%;
          bottom: 0%;
          width: 100vw;
          z-index: 1;
        }

        .right-leaf-with-branch {
          position: absolute;
          right: 0%;
          bottom: 0%;
          width: 50%;
          z-index: 16;
        }
      }

      .middle-section {
        z-index: 13;
        position: absolute;
        left: 50%;
        transform: translateX(-50%);
        bottom: 0;
        width: 100%;
        height: 100%;
        display: flex;
        justify-content: center;
        align-items: flex-end;

        .mid-leaves {
          position: absolute;
          left: 30%;
          bottom: 21%;
          width: 13%;
          z-index: 7;
        }

        .mid-left-leaf {
          position: absolute;
          left: -10%;
          bottom: 13%;
          width: 20%;
          z-index: 9;
        }

        .mid-right-leaf {
          position: absolute;
          right: -05%;
          bottom: 13%;
          width: 20%;
          z-index: 9;
        }
      }
    }
  }

  @media (min-width: 2500px) {
    .hero-section-container{
      .content{
        h6{
          color: var(--primary-color);
        }
        p{
          color: white;
        }
      }
    }
  }

  @media (min-width: 1920px) {
    .hero-section-container {
      .left-section {
        .tree-large {
          display: none;
        }
      }
      .right-section {
        .tree-right {
          display: none;
        }
        .shape-6{
          display: none;
        }
      }
    }
  }
  @media (max-width: 1024px) {
    // Tablet
    .hero-section-container {
      .graphics {
        .top {
          .left-cloud,
          .middle-cloud,
          .right-cloud {
            width: 100px;
          }
        }

        .left-section {
          .tree-large {
            top: 35%;
          }
          .tree-small {
            width: 20%;
            top: 60%;
          }
          .left-bg-leaf-with-branch {
            /* width: 13%; */
          }
          .left-leaf-bg {
            /* width: 8%; */
            bottom: 0%;
          }
          .left-small-leaves {
            /* width: 22%; */
            bottom: 2%;
          }
          .tiny-leaves {
            /* width: 6%; */
            bottom: 0;
          }
          .left-left-leaf,
          .left-right-leaf,
          .left-mid-leaf {
            /* width: 15%; */
            bottom: -1%;
          }
          .left-lg-leaf-with-branch {
            /* width: 38%; */
          }
        }

        .right-section {
          .tree-right {
            bottom: 20%;
          }
          .bg-leaves {
            /* width: 20%; */
          }
          .large-leaf {
            /* width: 13%; */
            bottom: 2%;
          }
          .right-small-leaves {
            /* width: 18%; */
            bottom: 2%;
          }
          .three-leaves {
            /* width: 8%; */
            bottom: 6%;
          }
          .two-leaves {
            /* width: 10%; */
          }
          .long-leaf-1,
          .long-leaf-2 {
            /* width: 15%; */
          }
        }
        .middle-section {
          .mid-leaves {
            bottom: 18%;
          }
        }

        .shape-1,
        .shape-6 {
          /* height: 90vh; */
        }
        .shape-2,
        .shape-3,
        .shape-4 {
          display: none;
        }
      }
    }
    .left-leaf-with-branch,
    .right-overflow-leaves {
      display: none;
    }
  }

  @media (max-width: 900px) {
    .hero-section-container {
      .graphics {
        .top {
          .middle-cloud {
            display: none;
          }
        }
        .left-section {
          .tree-large {
            top: 45%;
          }
        }
        .middle-section {
          .mid-leaves {
            display: none;
          }
        }
      }
    }
  }

  @media (max-width: 480px) {
    // Tablet
    .hero-section-container {
      .graphics {
        .top {
          .left-cloud,
          .middle-cloud,
          .right-cloud {
            width: 100px;
          }
        }

        .left-section {
          .tree-large {
            top: 70%;
          }
          .tree-small {
            width: 20%;
            top: 75%;
          }
          .left-bg-leaf-with-branch {
            /* width: 13%; */
          }
          .left-leaf-bg {
            /* width: 8%; */
            /* bottom: 5%; */
          }
          .left-small-leaves {
            /* width: 22%; */
            bottom: 2%;
          }
          .tiny-leaves {
            width: 45%;
            bottom: 0;
          }
          .left-left-leaf,
          .left-right-leaf,
          .left-mid-leaf {
            /* width: 15%; */
            bottom: -1%;
          }
          .left-lg-leaf-with-branch {
            /* width: 38%; */
          }
        }

        .right-section {
          .tree-right {
            bottom: 10%;
          }
          .bg-leaves {
            /* bottom: 7%; */
          }
          .large-leaf {
            bottom: 0%;
          }
          .right-small-leaves {
            /* width: 18%; */
            bottom: 2%;
          }
          .right-leaf-with-branch {
            /* bottom: 2%; */
          }
          .three-leaves {
            width: 8%;
            bottom: 3%;
          }
          .two-leaves {
            /* width: 10%; */
          }
          .long-leaf-1,
          .long-leaf-2 {
            /* width: 15%; */
          }
        }

        .shape-1,
        .shape-6 {
          /* width: 40%;
          height: auto; */
        }
        .middle-section {
          bottom: 2%;
          .mid-left-leaf,
          .mid-right-leaf {
            display: none;
          }
        }
      }
      .left-leaf-with-branch,
      .right-overflow-leaves {
        display: none;
      }
    }
  }
`;
